/**
 * Shared Chat Components - Reusable UI patterns for chat widgets
 *
 * Extracted from project-chat.tsx to enable DRY across all chat components.
 * These components handle common states (loading, empty, error) with consistent UX.
 *
 * @layer presentation
 * @arch_domain copilot
 */

import { Loader2, XCircle, type LucideIcon } from 'lucide-react';

// ============================================================
// State Components
// ============================================================

interface EmptyStateProps {
  icon: LucideIcon;
  title: string;
  subtitle?: string;
}

export function EmptyState({ icon: Icon, title, subtitle }: EmptyStateProps) {
  return (
    <div className="flex flex-col items-center justify-center h-full text-center text-muted-foreground">
      <Icon className="h-8 w-8 mb-2 opacity-50" />
      <p className="text-sm">{title}</p>
      {subtitle && <p className="text-xs mt-1">{subtitle}</p>}
    </div>
  );
}

interface LoadingStateProps {
  message?: string;
}

export function LoadingState({ message = 'Loading...' }: LoadingStateProps) {
  return (
    <div className="flex flex-col items-center justify-center h-full text-center text-muted-foreground">
      <Loader2 className="h-8 w-8 mb-2 animate-spin" />
      <p className="text-sm">{message}</p>
    </div>
  );
}

interface ErrorStateProps {
  message: string;
  detail?: string;
}

export function ErrorState({ message, detail }: ErrorStateProps) {
  return (
    <div className="flex flex-col items-center justify-center h-full text-center text-destructive">
      <XCircle className="h-8 w-8 mb-2" />
      <p className="text-sm">{message}</p>
      {detail && <p className="text-xs mt-1">{detail}</p>}
    </div>
  );
}
